import { Directive, ElementRef, Input, OnInit } from "@angular/core";
import { create, FilePond, FilePondOptions, FileStatus } from "filepond";
import { merge } from "lodash-es";
import { nextRuntimeId } from "src/app/support/runtime-id";

@Directive({
  selector: "[filePond]",
  standalone: false
})
export class FilePondDirective implements OnInit {
  @Input("filePond")
  config:
    | (FilePondOptions & {
        maxFileSize?: string;
        maxTotalFileSize?: string;
        acceptedFileTypes?: string[];
      })
    | undefined;

  @Input()
  filePondInitialFile: { name: string; size: number } | undefined;

  @Input()
  filePondInitialFiles:
    | Array<{ id: string; name: string; size: number }>
    | undefined;

  @Input()
  disablePaste: boolean | undefined = false;

  private filePond: FilePond | undefined;

  readonly defaultConfig: FilePondOptions = {
    storeAsFile: true,
    allowProcess: false,
    maxFiles: 1,
    allowMultiple: false,
    instantUpload: false,
    credits: false
  };

  constructor(private elementRef: ElementRef<HTMLInputElement>) {}

  ngOnInit(): void {
    const config = merge({}, this.defaultConfig, this.config || {});

    config.allowPaste = !this.disablePaste;

    if (this.filePondInitialFile) {
      config.files = [
        {
          source: nextRuntimeId(),
          options: {
            type: "local",
            file: {
              name: this.filePondInitialFile.name,
              type: "image/png",
              size: this.filePondInitialFile.size
            }
          }
        }
      ];
    }

    if (this.filePondInitialFiles) {
      config.files = this.filePondInitialFiles.map((x) => ({
        source: nextRuntimeId(),
        options: {
          type: "local",
          file: {
            id: x.id,
            name: x.name,
            type: "image/png",
            size: x.size || NaN
          }
        }
      }));
    }

    this.filePond = create(this.elementRef.nativeElement, config);
  }

  addUploadedFile({
    name,
    type,
    size
  }: {
    name: string;
    type: string;
    size: number;
  }): void {
    this.filePond?.addFile(name, {
      type: "local",
      file: {
        name,
        type,
        size: size || NaN
      }
    });
  }

  async startUpload(): Promise<void> {
    await this.filePond?.processFiles();
  }

  removeFile(id: string): void {
    this.filePond?.removeFile(id);
  }

  clearFiles(): void {
    this.filePond?.removeFiles();
  }

  clearInvalidFiles(): void {
    const files = this.filePond?.getFiles();
    if (files) {
      for (const file of files) {
        switch (file.status) {
          case FileStatus.PROCESSING_ERROR:
          case FileStatus.LOAD_ERROR: {
            this.filePond?.removeFile(file.id);
            break;
          }
        }
      }
    }
  }
}
